import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { ComplianceFiling, TaskAmountExtract } from '../../types';
import { useUpsertTaskExtract, amountKeysFor, exportFilingJournal, extractKeys, ApiError } from '../../services/complianceApi';
import { filedAmounts } from '../../services/displayAmounts';
import { filedOriginFor } from '../../utils/filedOrigin';
import { lineVariance } from '../../utils/reconcileVariance';
import { formatCurrency, formatDate } from '../../utils/complianceFormatters';
import { PAYMENT_STATUSES, paymentStatusFromExtracted, isNoPaymentDue } from '../../utils/paymentStatus';
import { CalendarDateInput } from '../dates/CalendarDateInput';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  table: {
    width: '100%',
  },
  deltaWarning: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  deltaOk: {
    color: tokens.colorNeutralForeground3,
  },
  estimateNote: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  payment: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseDecimal(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;   // Fix #3: was NaN
}

// Seed monetary fields to 2 decimal places (it's money) — except an exact 0, which stays "0"
// rather than "0.00" per Christian's preference; null/undefined seed empty.
function money2(v: number | null | undefined): string {
  if (v === undefined || v === null) return '';
  return v === 0 ? '0' : v.toFixed(2);
}

function seedForm(
  resolved: Record<string, number | null>,
  keys: readonly string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of keys) {
    out[key] = money2(resolved[key]);
  }
  return out;
}

// Fix #2 — single source of truth for seeding the payment block
interface PaymentForm {
  confirmationNumber: string;
  amountPaid: string;
  debitDate: string;
  bankAccountMasked: string;
  paymentStatus: string;
  notes: string;
}

function seedPayment(extract: TaskAmountExtract | undefined): PaymentForm {
  return {
    confirmationNumber: extract?.confirmationNumber ?? '',
    amountPaid: money2(extract?.amountPaid),
    debitDate: extract?.debitDate?.slice(0, 10) ?? '',
    bankAccountMasked: extract?.bankAccountMasked ?? '',
    paymentStatus: paymentStatusFromExtracted(extract?.paymentStatus),
    notes: extract?.notes ?? '',
  };
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface Props {
  taskId: string;
  /** Partition key — required by the export-journal proxy; optional so legacy call sites don't break. */
  clientId?: string;
  filing: ComplianceFiling;
  extract: TaskAmountExtract | undefined;
  children?: React.ReactNode;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ReconcileObligationCard({ taskId, clientId, filing, extract, children }: Props) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { accounts } = useMsal();

  // Fix #5 — useMemo gives a stable reference per obligationType; no blanket eslint-disable needed
  const amountKeys = useMemo(() => amountKeysFor(filing.obligationType), [filing.obligationType]);
  // The "filed" inputs seed from FILED figures only, never the draft (ruling 2026-10-26, DP-GRADE-PREFILL): the automated
  // estimate is not used for filing, and a value the preparer did not enter must never grade the suggestion against itself.
  const resolved = filedAmounts(extract);

  const [form, setForm] = useState<Record<string, string>>(() => seedForm(resolved, amountKeys));
  const [errors, setErrors] = useState<Record<string, string>>({});  // Fix #4
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fix #2 — single PaymentForm state seeded via seedPayment helper
  const [payment, setPayment] = useState<PaymentForm>(() => seedPayment(extract));

  const upsert = useUpsertTaskExtract();
  const qc = useQueryClient();

  // ── Xero manual-journal export (sales_tax + filed only; see the 2026-07-03 design) ──
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // 412 from the proxy — conversions' message names the missing G/L account roles.
  const [glMappingError, setGlMappingError] = useState<string | null>(null);
  // Re-export soft latch: exportedAt set → warn-and-confirm, never a hard block.
  const [confirmReexportOpen, setConfirmReexportOpen] = useState(false);

  const canExportJournal =
    filing.obligationType === 'sales_tax' && !!filing.filedAt && !!clientId;

  async function doExportJournal() {
    if (!clientId) return;
    setExportBusy(true);
    setExportError(null);
    try {
      const { blob, fileName } = await exportFilingJournal(taskId, filing.obligationId, clientId);
      // Same temporary object-URL + invisible-anchor idiom as the attachment download.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Refresh the extract so the "Exported …" badge picks up the new exportedAt stamp.
      void qc.invalidateQueries({ queryKey: extractKeys.byTask(taskId) });
    } catch (err) {
      if (err instanceof ApiError && err.status === 412) {
        setGlMappingError(err.message);
      } else {
        setExportError(err instanceof Error ? err.message : t('taskDetailPage.reconcile.export.failed'));
      }
    } finally {
      setExportBusy(false);
    }
  }

  function handleExportClick() {
    setExportError(null);
    if (extract?.exportedAt) {
      setConfirmReexportOpen(true);
    } else {
      void doExportJournal();
    }
  }

  // Fix #2 + Fix #5 — reseed when extract or amountKeys change; honest deps, no blanket disable
  useEffect(() => {
    setForm(seedForm(filedAmounts(extract), amountKeys));
    setPayment(seedPayment(extract));
  }, [extract, filing.obligationId, amountKeys]);

  // Live filed numbers for Δ computation (updates as user types)
  const currentFiledNumbers: Record<string, number | null> = {};
  for (const key of amountKeys) {
    currentFiledNumbers[key] = parseDecimal(form[key] ?? '');
  }

  // Fix #1 — reconciled ONLY when filed.totalDue or legacy amounts.totalDue is numeric
  const isReconciled = typeof (extract?.filed?.totalDue ?? extract?.amounts?.totalDue) === 'number';

  // Payment status: $0 owed → "no payment due" (derived); otherwise the chosen enum.
  const noPaymentDue = isNoPaymentDue(currentFiledNumbers['totalDue']);
  const effectiveStatus = noPaymentDue ? 'no_payment_due' : payment.paymentStatus;
  // When the client remits to the agency directly, PTC schedules no payment — hide the scheduling fields.
  const clientRemits = !!filing.clientHandlesPayment;

  function setField(key: string, value: string) {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));  // Fix #4: clear field error on change
  }

  function setPaymentField(key: keyof PaymentForm, value: string) {
    setPayment(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  }

  // Fix #4 + Fix #1 — validate by inspecting raw string, surface per-field errors (amounts + payment fields)
  function validate(): boolean {
    const next: Record<string, string> = {};
    for (const key of amountKeys) {
      const raw = (form[key] ?? '').trim();
      if (raw.length > 0 && !Number.isFinite(Number(raw))) next[key] = t('taskDetailPage.reconcile.validation.mustBeNumber');
    }
    // Validate amountPaid
    const rawAmountPaid = payment.amountPaid.trim();
    if (rawAmountPaid.length > 0 && !Number.isFinite(Number(rawAmountPaid))) {
      next['amountPaid'] = t('taskDetailPage.reconcile.validation.mustBeNumber');
    }
    // Validate debitDate
    const rawDebitDate = payment.debitDate.trim();
    if (rawDebitDate.length > 0 && !/^\d{4}-\d{2}-\d{2}$/.test(rawDebitDate)) {
      next['debitDate'] = t('taskDetailPage.reconcile.validation.mustBeDate');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSave() {
    if (!validate()) return;  // Fix #4: return early on invalid
    setSubmitError(null);

    const amounts: Record<string, number | null> = {};
    for (const key of amountKeys) {
      amounts[key] = parseDecimal(form[key] ?? '');
    }

    upsert.mutate(
      {
        taskId,
        obligationId: filing.obligationId,
        payload: {
          source: 'manual',
          amounts,
          // Grading loop (spec 2026-10-22 §2): where each value came from (typed, or carried forward from a saved filed figure),
          // so the suggestion is never graded against itself.
          filedOrigin: filedOriginFor(extract, amounts, amountKeys),
          enteredByName: accounts[0]?.name ?? accounts[0]?.username,
          enteredById: accounts[0]?.localAccountId,
          confirmationNumber: payment.confirmationNumber.trim() || null,
          amountPaid: parseDecimal(payment.amountPaid),
          debitDate: payment.debitDate || null,
          bankAccountMasked: payment.bankAccountMasked.trim() || null,
          paymentStatus: effectiveStatus,
          notes: payment.notes.trim() || null,
        },
      },
      {
        onSuccess: () => setSubmitError(null),
        onError: (err) =>
          setSubmitError(err instanceof Error ? err.message : t('taskDetailPage.reconcile.saveFailed')),
      },
    );
  }

  return (
    <div className={styles.card}>
      {/* Upload slots injected by the page */}
      {children}

      {/* Status badge */}
      <div className={styles.header}>
        <Badge
          appearance="filled"
          color={isReconciled ? 'success' : 'warning'}
        >
          {isReconciled
            ? t('taskDetailPage.reconcile.status.reconciled')
            : t('taskDetailPage.reconcile.status.pending')}
        </Badge>
        {extract?.exportedAt && (
          <Badge appearance="outline" color="informative">
            {extract.exportedByName
              ? t('taskDetailPage.reconcile.export.exportedBadge', {
                  date: formatDate(extract.exportedAt),
                  name: extract.exportedByName,
                })
              : t('taskDetailPage.reconcile.export.exportedBadgeNoName', {
                  date: formatDate(extract.exportedAt),
                })}
          </Badge>
        )}
      </div>

      {/* Computed | Filed | Δ table */}
      <Table className={styles.table} size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>{/* line label */}</TableHeaderCell>
            <TableHeaderCell>{t('taskDetailPage.reconcile.columns.computed')}</TableHeaderCell>
            <TableHeaderCell>{t('taskDetailPage.reconcile.columns.filed')}</TableHeaderCell>
            <TableHeaderCell>{t('taskDetailPage.reconcile.columns.variance')}</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {amountKeys.map((key) => {
            const computedVal = extract?.computed?.[key] ?? null;
            const delta = lineVariance(extract?.computed, currentFiledNumbers, key);

            return (
              <TableRow key={key}>
                <TableCell>{t(`amounts.${key}`)}</TableCell>
                <TableCell>
                  {typeof computedVal === 'number' ? formatCurrency(computedVal) : '—'}
                </TableCell>
                <TableCell>
                  <Field label="" validationMessage={errors[key]}>
                    <Input
                      type="number"
                      value={form[key] ?? ''}
                      onChange={(_e, data) => setField(key, data.value)}
                      disabled={upsert.isPending}
                      size="small"
                    />
                  </Field>
                </TableCell>
                <TableCell>
                  {delta !== null ? (
                    <span className={delta !== 0 ? styles.deltaWarning : styles.deltaOk}>
                      {formatCurrency(delta)}
                    </span>
                  ) : (
                    <span className={styles.deltaOk}>{'—'}</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <span className={styles.estimateNote}>{t('taskDetailPage.reconcile.estimateNote')}</span>

      {/* Confirmation & payment block — editable */}
      <div className={styles.payment}>
        <strong>{t('taskDetailPage.reconcile.payment.title')}</strong>
        <Field label={t('taskDetailPage.reconcile.payment.confNumber')}>
          <Input
            value={payment.confirmationNumber}
            onChange={(_e, data) => setPaymentField('confirmationNumber', data.value)}
            disabled={upsert.isPending}
          />
        </Field>
        {clientRemits ? (
          <em>{t('taskDetailPage.reconcile.payment.clientRemits')}</em>
        ) : (
          <>
            <Field label={t('taskDetailPage.reconcile.payment.amountPaid')} validationMessage={errors['amountPaid']}>
              <Input
                type="number"
                value={payment.amountPaid}
                onChange={(_e, data) => setPaymentField('amountPaid', data.value)}
                disabled={upsert.isPending}
              />
            </Field>
            <Field label={t('taskDetailPage.reconcile.payment.debitDate')} validationMessage={errors['debitDate']}>
              <CalendarDateInput
                value={payment.debitDate}
                onChange={(v) => setPaymentField('debitDate', v)}
                disabled={upsert.isPending}
              />
            </Field>
            <Field label={t('taskDetailPage.reconcile.payment.account')}>
              <Input
                value={payment.bankAccountMasked}
                onChange={(_e, data) => setPaymentField('bankAccountMasked', data.value)}
                disabled={upsert.isPending}
              />
            </Field>
            <Field label={t('taskDetailPage.reconcile.payment.status')}>
              <Dropdown
                value={t(`taskDetailPage.reconcile.payment.statusOption.${effectiveStatus}`)}
                selectedOptions={[effectiveStatus]}
                disabled={upsert.isPending || noPaymentDue}
                onOptionSelect={(_e, data) => setPaymentField('paymentStatus', data.optionValue ?? 'not_scheduled')}
              >
                {PAYMENT_STATUSES.map((s) => (
                  <Option key={s} value={s}>{t(`taskDetailPage.reconcile.payment.statusOption.${s}`)}</Option>
                ))}
              </Dropdown>
            </Field>
          </>
        )}
        <Field label={t('taskDetailPage.reconcile.payment.notesLabel')}>
          <Textarea
            value={payment.notes}
            onChange={(_e, data) => setPaymentField('notes', data.value)}
            disabled={upsert.isPending}
          />
        </Field>
      </div>

      {/* Save error */}
      {submitError && (
        <MessageBar intent="error">
          <MessageBarBody>{submitError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Export error (non-412 failures; 412 opens the G/L mapping dialog instead) */}
      {exportError && (
        <MessageBar intent="error">
          <MessageBarBody>{exportError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Save button + Xero journal export (sales_tax + filed only) */}
      <div className={styles.footer}>
        <Button
          appearance="primary"
          disabled={upsert.isPending}
          icon={upsert.isPending ? <Spinner size="tiny" /> : undefined}
          onClick={handleSave}
        >
          {t('taskDetailPage.reconcile.save')}
        </Button>
        {canExportJournal && (
          <Button
            appearance="secondary"
            disabled={exportBusy}
            icon={exportBusy ? <Spinner size="tiny" /> : undefined}
            onClick={handleExportClick}
          >
            {t('taskDetailPage.reconcile.export.button')}
          </Button>
        )}
      </div>

      {/* 412 — the client's G/L map in Conversions lacks roles this journal needs */}
      <Dialog
        open={glMappingError !== null}
        onOpenChange={(_e, d) => { if (!d.open) setGlMappingError(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('taskDetailPage.reconcile.export.glDialog.title')}</DialogTitle>
            <DialogContent>
              {/* Conversions' message names the missing account roles — relay it verbatim. */}
              <p>{glMappingError}</p>
              <p>{t('taskDetailPage.reconcile.export.glDialog.hint')}</p>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setGlMappingError(null)}>
                {t('taskDetailPage.reconcile.export.glDialog.close')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Re-export confirm — exportedAt is a soft latch (warn-and-confirm, never a hard block) */}
      <Dialog
        open={confirmReexportOpen}
        onOpenChange={(_e, d) => { if (!d.open) setConfirmReexportOpen(false); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('taskDetailPage.reconcile.export.confirm.title')}</DialogTitle>
            <DialogContent>
              {t('taskDetailPage.reconcile.export.confirm.body', {
                date: extract?.exportedAt ? formatDate(extract.exportedAt) : '',
              })}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmReexportOpen(false)}>
                {t('taskDetailPage.reconcile.export.confirm.cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  setConfirmReexportOpen(false);
                  void doExportJournal();
                }}
              >
                {t('taskDetailPage.reconcile.export.confirm.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
